/**
 * Batch E commit E2 — SupervisorMobileView.
 *
 * Phone-friendly master view that replaces the AgentGrid + CallMonitor
 * panels at phone breakpoint. Provides a tab toggle between Agents and
 * Calls, with each tab rendering a vertical list of tappable cards
 * that drill into the corresponding detail route:
 *
 *   - tap an agent card → /supervisor/agents/:agentId
 *   - tap a call card   → /supervisor/calls/:callId
 *
 * The store wiring lives on `SupervisorDashboard`; this view only
 * reads `agentList` and `liveCalls`, so it works as a child of any
 * route that has the subscription mounted.
 */
import { useState, type JSX } from 'react'
import { useNavigate } from 'react-router'
import { Tag } from 'primereact/tag'
import { SelectButton } from 'primereact/selectbutton'
import type { AgentStatus } from '@kc3/types'
import { useSupervisorStore } from '../../store/supervisorStore.js'

type Severity = 'success' | 'warning' | 'danger' | 'info' | 'secondary'

const STATUS_SEVERITY: Record<AgentStatus, Severity> = {
  IDLE: 'success',
  AVAILABLE_ON_DEMAND: 'success',
  IN_CALL: 'info',
  RINGING: 'warning',
  PAUSED: 'warning',
  ON_BREAK: 'secondary',
  ACW: 'warning',
  OFFLINE: 'danger',
  // Reservation FSM states; map is exhaustive over AgentStatus.
  REGISTERED: 'secondary',
  RESERVING: 'warning',
  RESERVED: 'warning',
  INVITING: 'warning',
  ANSWERED: 'info',
}

type Tab = 'agents' | 'calls'

const TAB_OPTIONS = [
  { label: 'Agents', value: 'agents' as const },
  { label: 'Calls', value: 'calls' as const },
]

function formatDuration(sec: number): string {
  const m = Math.floor(sec / 60)
  const s = String(sec % 60).padStart(2, '0')
  return `${m}:${s}`
}

export default function SupervisorMobileView(): JSX.Element {
  const agents = useSupervisorStore((s) => s.agentList)
  const calls = useSupervisorStore((s) => s.liveCalls)
  const [tab, setTab] = useState<Tab>('agents')
  const navigate = useNavigate()

  return (
    <div
      className="flex h-full min-h-0 flex-col gap-3 p-2"
      data-testid="supervisor-mobile-view"
    >
      <SelectButton
        value={tab}
        onChange={(e) => {
          // PrimeReact SelectButton emits null when the same option is
          // clicked twice; keep the current tab in that case.
          if (e.value) setTab(e.value as Tab)
        }}
        options={TAB_OPTIONS}
        aria-label="Toggle between agents and active calls"
      />

      <div className="flex-1 min-h-0 overflow-y-auto">
        {tab === 'agents' && (
          <ul className="flex flex-col gap-2">
            {agents.length === 0 && (
              <li className="rounded border border-gray-200 bg-white p-4 text-center text-sm text-gray-500">
                No agents online.
              </li>
            )}
            {agents.map((agent) => (
              <li key={agent.id}>
                <button
                  type="button"
                  data-testid={`agent-card-${agent.id}`}
                  onClick={() => navigate(`/supervisor/agents/${agent.id}`)}
                  className="flex w-full items-center justify-between gap-3 rounded border border-gray-200 bg-white p-3 text-left shadow-sm active:bg-gray-50"
                >
                  <div className="min-w-0">
                    <div className="truncate font-semibold text-sm">
                      {agent.name}
                    </div>
                    <div className="text-xs text-gray-500">
                      Calls: {agent.callsHandled}
                    </div>
                  </div>
                  <Tag
                    value={agent.status}
                    severity={STATUS_SEVERITY[agent.status] ?? 'secondary'}
                  />
                </button>
              </li>
            ))}
          </ul>
        )}

        {tab === 'calls' && (
          <ul className="flex flex-col gap-2">
            {calls.length === 0 && (
              <li className="rounded border border-gray-200 bg-white p-4 text-center text-sm text-gray-500">
                No active calls.
              </li>
            )}
            {calls.map((call) => (
              <li key={call.callId}>
                <button
                  type="button"
                  data-testid={`call-card-${call.callId}`}
                  onClick={() => navigate(`/supervisor/calls/${call.callId}`)}
                  className="flex w-full items-center justify-between gap-3 rounded border border-gray-200 bg-white p-3 text-left shadow-sm active:bg-gray-50"
                >
                  <div className="min-w-0">
                    <div className="truncate font-semibold text-sm">
                      {call.callerCLI}
                    </div>
                    <div className="truncate text-xs text-gray-500">
                      {call.agentName}
                    </div>
                  </div>
                  <span className="shrink-0 text-xs text-blue-600">
                    {formatDuration(call.durationSec)}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  )
}
